// ** React Imports

// ** Axios
import axios from 'axios';

// ** Next Import
import { useRouter } from 'next/router';
import { createContext, useEffect, useState } from 'react';

// ** Config
import authConfig from 'src/configs/auth';

// ** Import getHomeRoute
import getHomeRoute from 'src/layouts/components/acl/getHomeRoute';

// ** Defaults
const defaultProvider = {
  user: null,
  setUser: () => null,
  campaign: null,
  setCampaign: () => null,
  slug: null,
  setSlug: () => null,
  loading: true,
  setLoading: () => Boolean,
  isInitialized: false,
  setIsInitialized: () => Boolean,
  UserStatus: 1,
  setUserStatus: () => null,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  requiresPasswordChange: false,
  setRequiresPasswordChange: () => Boolean,
  updatePassword: () => Promise.resolve({ success: false, error: '' }),
  pendingLoginData: null,
};
const AuthContext = createContext(defaultProvider);

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user);
  const [campaign, setCampaign] = useState(defaultProvider.campaign);
  const [slug, setSlug] = useState(defaultProvider.slug);
  const [loading, setLoading] = useState(defaultProvider.loading);
  const [isInitialized, setIsInitialized] = useState(
    defaultProvider.isInitialized
  );
  const [UserStatus, setUserStatus] = useState(defaultProvider.UserStatus);
  const [apiData, setApiData] = useState([]);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  // ** Hooks
  const router = useRouter();
  useEffect(() => {
    const initAuth = async () => {
      // Small delay to ensure localStorage is ready after page load
      // This prevents race conditions with SSR and client-side hydration
      await new Promise((resolve) => setTimeout(resolve, 50));

      try {
        const storedToken = window.localStorage.getItem(
          authConfig.storageTokenKeyName || 'accessToken'
        );

        if (storedToken) {
          // console.log("storedToken => ", storedToken);
          setLoading(true);

          const response = await axios.get(authConfig.meEndpoint, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
              'Content-Type': 'application/json',
            },
          });

          setUser({ ...response?.data?.userData });
          // console.log("response.data ===> ", response.data);

          if (response?.data?.userData?.role === 'camp_admin') {
            const slugResponse = await axios.get(authConfig.createCampaign);
            setCampaign({ ...response?.data?.campDetails[0] });

            const slugData = slugResponse.data.filter(
              (x) => x.id === response?.data?.campDetails[0].campaign_id
            );
            // console.log("SLUG RESPONSE ===> ", slugResponse, slugData);

            setSlug(slugData);
          }

          setLoading(false);
          setIsInitialized(true);
        } else {
          // No token found, but initialization is complete
          setLoading(false);
          setIsInitialized(true);
        }
      } catch (error) {
        // Token validation failed
        console.error('Auth initialization error:', error);
        localStorage.removeItem('userData');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        setUser(null);
        setLoading(false);
        setIsInitialized(true);

        if (
          (authConfig.onTokenExpiration === 'logout' ||
            authConfig.onTokenExpiration === 'refreshToken') &&
          !router.pathname.includes('login')
        ) {
          router.replace('/login');
        }
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // login hangled//////////////////////////////////

  const handleLogin = (params, errorCallback) => {
    // Set loading state to prevent guards from interfering
    setLoading(true);

    axios
      .post(authConfig.loginEndpoint, params)
      .then(async (response) => {
        // Check if user needs to change password FIRST (before storing anything)
        const isFirstLogin = response?.data?.userData?.is_first_login;
        if (isFirstLogin === false) {
          // Don't store token or user data - password change required first
          // Clear any existing tokens to prevent auto-login
          window.localStorage.removeItem(authConfig.storageTokenKeyName || 'accessToken');
          window.localStorage.removeItem('refreshToken');
          window.localStorage.removeItem('userData');
          
          // Store login credentials for re-login after password change
          setPendingLoginData({ email: params.email, password: params.password, rememberMe: params.rememberMe });
          setRequiresPasswordChange(true);
          setUser(null); // Ensure user is null
          setLoading(false);
          return;
        }

        // Store all data in localStorage first (before updating state)
        // This prevents re-renders during the storage process

        // Always store the access token
        window.localStorage.setItem(
          authConfig.storageTokenKeyName || 'accessToken',
          response.data.accessToken
        );

        // If rememberMe is true, also store the refresh token
        if (params.rememberMe && response.data.refreshToken) {
          window.localStorage.setItem(
            'refreshToken',
            response.data.refreshToken
          );
        }

        response?.data?.userData &&
          window.localStorage.setItem(
            'userData',
            JSON.stringify(response?.data?.userData)
          );

        if (response?.data?.userData?.role === 'camp_admin') {
          const slugResponse = await axios.get(authConfig.createCampaign);
          const slugData = slugResponse.data.filter(
            (x) => x.id === response?.data?.campDetails[0].campaign_id
          );

          (slugData !== null || slugData !== undefined) &&
            window.localStorage.setItem('slugData', JSON.stringify(slugData));

          response?.data?.campDetails &&
            window.localStorage.setItem(
              'campDetails',
              JSON.stringify(response?.data?.campDetails[0])
            );
          response?.data?.campOrganizationDetails &&
            window.localStorage.setItem(
              'campOrganizationDetails',
              JSON.stringify(response.data.campOrganizationDetails[0])
            );

          // Update state after all localStorage operations
          setCampaign({ ...response?.data?.campDetails[0] });
          setSlug(slugData);
        }

        // Update user state last
        setUser({ ...response?.data?.userData });

        // Small delay to ensure state updates are processed
        setTimeout(() => {
          setLoading(false);
          const returnUrl = router.query.returnUrl;

          // Determine the appropriate redirect URL based on user role
          let redirectURL = '/';
          if (
            returnUrl &&
            returnUrl !== '/' &&
            returnUrl !== '/login'
          ) {
            redirectURL =
              typeof returnUrl === 'string'
                ? returnUrl
                : Array.isArray(returnUrl)
                  ? returnUrl[0]
                  : '/';
          } else if (response?.data?.userData?.role === 'camp_admin') {
            redirectURL = '/campadmin/home';
          } else if (response?.data?.userData?.role === 'organization') {
            redirectURL = '/organization/home';
          } else if (response?.data?.userData?.role === 'donor') {
            redirectURL = '/allcampaigns';
          } else if (response?.data?.userData?.role === 'super_admin') {
            redirectURL = getHomeRoute('super_admin'); // This will return "/admin/dashboards"
          }

          // Use push instead of replace to avoid route cancellation
          router.push(redirectURL);
        }, 100);
      })
      .catch((err) => {
        setLoading(false);
        if (errorCallback) errorCallback(err);
      });
  };

  const handleLogout = () => {
    setUser(null);
    setCampaign(null);
    setSlug(null);
    window.localStorage.removeItem('userData');
    window.localStorage.removeItem('slugData');
    window.localStorage.removeItem('campDetails');
    window.localStorage.removeItem('campOrganizationDetails');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem(
      authConfig.storageTokenKeyName || 'accessToken'
    );
    router.push('/');
  };

  const handleSetUserStatus = (newStatus) => {
    setUserStatus(newStatus);
  };

  const handleUpdatePassword = async (email, newPassword) => {
    try {
      const response = await axios.put(
        authConfig.updatePasswordByEmailEndpoint,
        {
          email,
          new_password: newPassword,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        // Password updated successfully, now re-login
        const currentPendingData = pendingLoginData;
        if (currentPendingData) {
          // Use the new password for login
          const loginParams = {
            email: currentPendingData.email,
            password: newPassword,
            rememberMe: currentPendingData.rememberMe,
          };
          
          // Clear password change state
          setRequiresPasswordChange(false);
          setPendingLoginData(null);
          
          // Re-login with new password
          handleLogin(loginParams);
        }
        return { success: true, data: response.data };
      }
      return { success: false, error: 'Unexpected response status' };
    } catch (error) {
      console.error('Password update error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update password',
      };
    }
  };

  const values = {
    user,
    setUser,
    campaign,
    setCampaign,
    slug,
    setSlug,
    loading,
    setLoading,
    isInitialized,
    setIsInitialized,
    UserStatus,
    setUserStatus: handleSetUserStatus,
    login: handleLogin,
    logout: handleLogout,
    requiresPasswordChange,
    setRequiresPasswordChange,
    updatePassword: handleUpdatePassword,
    pendingLoginData,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
